import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2, LogIn } from 'lucide-react';
import { clientAuthApi } from '../services/clientAuthApi';

/**
 * Public page reached via the email verification link.
 * URL: /verify-email?token=...
 *
 * - Calls POST /api/client-auth/verify-email with the token
 * - Shows success or error
 * - Provides a link to log in
 */
export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t('verifyEmail.noToken', 'No verification token provided.'));
      return;
    }

    (async () => {
      try {
        await clientAuthApi.verifyEmail({ token });
        setStatus('success');
      } catch (err: any) {
        setStatus('error');
        setErrorMessage(err.message || t('verifyEmail.failed', 'Email verification failed.'));
      }
    })();
  }, [token, t]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-textPrimary">
              {t('verifyEmail.verifying', 'Verifying your email...')}
            </h1>
          </>
        )}

        {status === 'success' && (
          <div className="space-y-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-textPrimary">
              {t('verifyEmail.successTitle', 'Email verified!')}
            </h1>
            <p className="text-textSecondary">
              {t('verifyEmail.successMessage', 'Your email address has been verified successfully. You can now log in to your account.')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors"
            >
              <LogIn className="w-4 h-4" />
              {t('verifyEmail.loginButton', 'Log In')}
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-textPrimary">
              {t('verifyEmail.errorTitle', 'Verification failed')}
            </h1>
            <p className="text-textSecondary">
              {errorMessage}
            </p>
            <p className="text-sm text-textSecondary">
              {t('verifyEmail.errorHint', 'The link may have expired or already been used. You can request a new verification email after logging in.')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors"
            >
              <LogIn className="w-4 h-4" />
              {t('verifyEmail.loginButton', 'Log In')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
